'use client';

type PCInitOptions = { shop_id?: string };
type PCEventPayload = Record<string, any>;

declare global {
  interface Window {
    personaclick?: ((...args: any[]) => void) & { q?: any[] };
    PersonaClick?: {
      init: (optsOrId: PCInitOptions | string) => void;
      identify?: (user: Record<string, any>) => void;
      event?: (name: string, payload?: PCEventPayload) => void;
      track?: (name: string, payload?: any) => void;
      profile?: (action: 'set' | string, params: any, success?: (res:any)=>void, error?: (err:any)=>void) => void;
    };
  }
}

let _shopId: string | undefined;
let _initialized = false;

const _identifyQueue: Array<Record<string, any>> = [];
const _eventQueue: Array<{ name: string; payload?: PCEventPayload }> = [];

const PROFILE_CACHE_KEY = '__pc_profile_last__';

let _lastUrl: string | undefined;
const getReferer = () =>
  typeof document !== 'undefined' ? _lastUrl || document.referrer || undefined : undefined;

export function setShopId(shopId: string) {
  _shopId = shopId;
  tryInit();
}

function ensureAdapter() {
  if (!window.PersonaClick) {
    window.PersonaClick = {
      init: (optsOrId: PCInitOptions | string) =>
        window.personaclick && window.personaclick('init', optsOrId),

      event: (name: string, payload?: PCEventPayload) =>
        window.personaclick && window.personaclick('track', name, payload),

      identify: (user: Record<string, any>) =>
        window.personaclick && window.personaclick('identify', user),

      track: (name: string, payload?: any) =>
        window.personaclick && window.personaclick('track', name, payload),

      profile: (action: 'set' | string, params: any, success?: (res:any)=>void, error?: (err:any)=>void) =>
        (window.personaclick as any) && (window.personaclick as any)('profile', action, params, success, error),
    };
  }
}

function ensureBootstrap() {
  if (!window.personaclick) {
    window.personaclick = function (...args: any[]) {
      (window.personaclick!.q = window.personaclick!.q || []).push(args);
    } as any;
  }

  if (_shopId && !(window as any).__pc_init_queued) {
    window.personaclick('init', _shopId);
    (window as any).__pc_init_queued = true;
  }

  if (!document.querySelector('script[data-pc-sdk="v3"]')) {
    const s = document.createElement('script');
    s.src = '//cdn.personaclick.com/v3.js';
    s.defer = true;
    s.setAttribute('data-pc-sdk', 'v3');
    document.head.appendChild(s);
  }
}

function tryInit() {
  if (_initialized) return;
  if (typeof window === 'undefined') return;
  if (!_shopId) return;

  ensureAdapter();
  ensureBootstrap();

  _initialized = true;
  drainQueues();
}

function drainQueues() {
  while (_identifyQueue.length) {
    const u = _identifyQueue.shift()!;
    if (window.PersonaClick?.identify) window.PersonaClick.identify(u);
    else window.PersonaClick?.track?.('identify', u);
  }

  while (_eventQueue.length) {
    const e = _eventQueue.shift()!;
    if (e.name === '__PROFILE_SET__') {
      (window.PersonaClick as any)?.profile?.('set', e.payload);
      rememberLastProfile(e.payload);
      continue;
    }
    window.PersonaClick?.track?.(e.name, e.payload);
  }
}

function pushTrack(name: string, payload?: any) {
  if (!_initialized) {
    _eventQueue.push({ name, payload });
    tryInit();
    return;
  }
  window.PersonaClick?.track?.(name, payload);
}

// ---------------------- PUBLIC API ----------------------

export function pcPageView({ url }: { url: string }) {
  _lastUrl = url;
}

export function pcViewItem(product: any) {
  const id = product?.sku;
  if (!id) return;
  pushTrack('view', { id: String(id) });
}

export function pcCategoryView(categoryId: string | number) {
  pushTrack('category', String(categoryId));
}

type PCCartRow = { sku: string | number; quantity?: number };

export function pcCart(list: PCCartRow[]) {
  if (!Array.isArray(list)) return;

  const payload = list
    .map((it) => {
      const id = it?.sku != null ? String(it.sku).trim() : '';
      if (!id) return null;
      const q = Math.round(Number(it.quantity ?? 1));
      const amount = Number.isFinite(q) && q > 0 ? q : 1;
      return { id, amount };
    })
    .filter(Boolean);

  pushTrack('cart', payload);
}

export function pcAddToCart(product: any, amount?: number, basket?: any) {
  const id = product?.sku;
  if (!id) return;

  let finalAmount: number;

  if (typeof amount === "number") {
    finalAmount = amount;
  } else {
    const existing = basket?.basketitem_set?.find((i: any) => {
      const existingId = i.product?.sku || i.product?.pk || i.product?.id || i.product?.code;
      return String(existingId) === String(id);
    });
    finalAmount = (existing?.quantity || 0) + 1;
  }

  const params: any = {
    id: String(id),
    amount: finalAmount,
  };

  if (product?.price) {
    params.price = Number(product.price);
  }

  pushTrack("cart", [params]);
}

export function pcRemoveFromCart(product: any) {
  const id = product?.sku;
  if (!id) return;
  pushTrack('remove_from_cart', String(id));
}

export function pcPurchase(order: any) {
  const products =
    (order?.orderitem_set || [])
      .map((it: any) => {
        const id = String(it?.product?.sku ?? '').trim();
        if (!id) return null;
        const amount = Math.max(1, Math.round(Number(it?.quantity ?? 1)));
        const price = Number(it?.price ?? it?.product?.price ?? 0);
        return { id, amount, price };
      })
      .filter(Boolean);

  if (products.length === 0) return;

  const params: any = {
    products,
  };

  if (order?.number) params.order = String(order.number);

  if (order?.amount != null) params.order_price = Number(order.amount);
  if (order?.shipping_amount) params.order_delivery = Number(order.shipping_amount);

  if (order?.discount_amount != null) {
    params.discount_amount = Number(order.discount_amount);
  }

  if (order?.coupon) params.promocode = String(order.coupon);
  if (order?.payment_option?.name) params.payment_type = String(order.payment_option.name);
  if (order?.shipping_option_slug) params.delivery_type = String(order.shipping_option_slug);

  const custom: any = {};
  if (order?.currency?.value) custom.currency = String(order.currency.value).toUpperCase();
  if (order?.tax != null) custom.tax = Number(order.tax);
  if (order?.shipping_option_slug) custom.shipping_name = String(order.shipping_option_slug);
  if (Object.keys(custom).length) params.custom = custom;

  pushTrack('purchase', params);
}

export function pcSearch(query: string) {
  pushTrack('search', String(query || ''));
}

export function pcAddToWishlist(product: any) {
  const id = product?.sku;
  if (!id) return;
  pushTrack('wish', String(id));
}

export function pcRemoveFromWishlist(product: any) {
  const id = product?.sku || product?.product?.sku;
  if (!id) return;
  pushTrack('remove_wish', String(id));
}

type ProductLike = { sku?: string | number };
export function pcWishlist(products: ProductLike[]) {
  if (!Array.isArray(products) || products.length === 0) return;

  const ids = products
    .map((p) => (p?.sku == null ? '' : String(p.sku).trim()))
    .filter((id) => id.length > 0);

  if (ids.length === 0) return;
  pushTrack('wish', ids);
}

export function pcCustom(eventKey: string, params?: any) {
  pushTrack(eventKey, params);
}

export function pcIdentify(user: Record<string, any>) {
  if (!_initialized) { _identifyQueue.push(user); tryInit(); return; }
  if (window.PersonaClick?.identify) window.PersonaClick.identify(user);
  else pushTrack('identify', user);
}

// ================= PROFILE SET ================= //

function normalizePhoneNumber(raw: string): string | undefined {
  if (!raw) return undefined;
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return '0' + digits;
  if (digits.length === 11 && digits.startsWith('0')) return digits;
  return undefined;
}

function normalizeProfileParams(p: any) {
  if (!p || typeof p !== 'object') return null;

  const out: any = {
    email: p.email?.trim(),
    phone: normalizePhoneNumber(p.phone),
    id: p.id != null ? String(p.id).trim() : undefined,
    first_name: p.first_name?.trim(),
    last_name: p.last_name?.trim(),
    gender: p.gender?.trim()?.toLowerCase(),
    birthday: p.date_of_birth || undefined,
  };

  if (out.gender && !['m', 'f'].includes(out.gender)) {
    delete out.gender;
  }

  Object.keys(out).forEach((key) => {
    if (!out[key]) delete out[key];
  });

  return Object.keys(out).length ? out : null;
}

function rememberLastProfile(obj: any) {
  // eslint-disable-next-line no-empty
  try { sessionStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(obj)); } catch {}
}

export function pcProfileSet(
  params: {
    email?: string;
    phone?: string;
    id?: string | number;
    first_name?: string;
    last_name?: string;
    gender?: 'm' | 'f' | string;
    date_of_birth?: string;
  },
  callbacks?: { success?: (res:any)=>void; error?: (err:any)=>void }
) {
  const clean = normalizeProfileParams(params);
  if (!clean) return;

  if (!_initialized) {
    _eventQueue.push({ name: '__PROFILE_SET__', payload: clean });
    tryInit();
    return;
  }

  (window.PersonaClick as any)?.profile?.('set', clean, callbacks?.success, callbacks?.error);
  rememberLastProfile(clean);
}

export function pcSubscriptionManage(profile: any) {
  if (typeof window === 'undefined' || !window.personaclick) return;

  const email = profile?.email;
  const phone = profile?.phone;

  if (!email && !phone) return;

  const payload = {
    email,
    phone,

    email_bulk: !!profile?.email_allowed,
    email_chain: !!profile?.email_allowed,
    email_transactional: true,

    sms_bulk: !!profile?.sms_allowed,
    sms_chain: !!profile?.sms_allowed,
    sms_transactional: true,
  };

  window.personaclick('subscription', 'manage', payload);
}

export function pcSubscribeBackInStock({
  email,
  phone,
  productId,
}: {
  email?: string;
  phone?: string;
  productId: string | number;
}) {
  if (!window.personaclick) return;

  window.personaclick("subscribe_trigger", "product_available", {
    email,
    phone,
    item: String(productId),
  });
}

export function pcUnsubscribeBackInStock({
  email,
  productIds = [],
}: {
  email: string;
  productIds?: Array<string | number>;
}) {
  if (typeof window === 'undefined' || !(window as any).personaclick) return;

  try {
    (window as any).personaclick('unsubscribe_trigger', 'product_available', {
      item_ids: productIds.map(String),
      email,
    });
  } catch (err) {
    console.warn('[PersonaClick] unsubscribe_trigger failed:', err);
  }
}

export function pcSubscribePriceDrop({
  email,
  phone,
  productId,
  price,
}: {
  email?: string;
  phone?: string;
  productId: string | number;
  price: number | string;
}) {
  if (!window.personaclick || !productId) return;

  window.personaclick("subscribe_trigger", "product_price_decrease", {
    item: String(productId),
    email,
    phone,
    price: Number(price),
  });
}

